'use client';

import {
  Alert,
  AlertDescription,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@multiplicu/bedrock';
import { type LoginFormValues, loginSchema } from './login.schema';
// BS-2151: form-level analytics emitters. step_view fires on mount,
// step_complete on the happy-path submit; the login step also drives
// the funnel-entry signal for PSA's "did anyone start the form" metric.
// BS-2152: emitSessionLinked fires after a successful member login to
// back-fill linked_application_id + linked_at on the session row.
import { emitEvent, emitSessionLinked, useStepAnalytics } from '~/lib/analytics';
import { useEffect, useState } from 'react';

import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { LOAN_TYPES } from '~/lib/consts/application';
import { login } from '~/lib/api/auth-helper';
import { useForm } from 'react-hook-form';
import { useFormContext } from '~/lib/providers/form-context';
import { useRouter } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';

const LOAN_TYPE_MAP: Record<string, number> = {
  gsap: LOAN_TYPES.GSAP,
};

function LoginFormContent() {
  const { handleLoginSuccess, resetForm, goToNextStep, setApplication } = useFormContext();
  const router = useRouter();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const preLoanType = typeof window !== 'undefined' ? sessionStorage.getItem('preLoanType') : null;

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // BS-2151: emit step_view + validation_error events for this step.
  useStepAnalytics({ step: 'login', formState: form.formState });

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  async function onSubmit(values: LoginFormValues) {
    setSubmitError(null);
    const result = await login(values.username, values.password);
    if (result.tokens) {
      // BS-2151: step_complete only after the login succeeded.
      // A failed login (wrong password) is NOT a step completion.
      emitEvent('step_complete', 'login');
      await handleLoginSuccess(result.tokens);
      if (preLoanType) {
        const loanType = LOAN_TYPE_MAP[preLoanType] ?? parseInt(preLoanType, 10);
        setApplication((prev) => ({ ...prev, loanType }));
        sessionStorage.removeItem('preLoanType');
        router.push('/0/personal-info');
      } else {
        goToNextStep();
      }
    } else if (result.error) {
      setSubmitError(result.error);
    }
  }

  function handleStartGuest() {
    // BS-2151: starting as guest is also a login-step completion
    // (the funnel rule is "did the member leave the login screen
    // on a happy path"). Emit before the navigation so the event
    // is queued while the route is still /login.
    emitEvent('step_complete', 'login');
    if (preLoanType) {
      const loanType = LOAN_TYPE_MAP[preLoanType] ?? parseInt(preLoanType, 10);
      setApplication((prev) => ({ ...prev, loanType }));
      sessionStorage.removeItem('preLoanType');
      router.push('/0/personal-info');
    } else {
      goToNextStep();
    }
  }

  const { isSubmitting } = form.formState;

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-y-10">
      <header className="flex flex-col gap-y-3">
        <h1 className="font-serif text-4xl font-medium tracking-tight text-balance text-fg sm:text-5xl">
          Welcome back
        </h1>
      </header>

      <Form {...form}>
        <form
          className="flex flex-col gap-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit(onSubmit)(e);
          }}
        >
          {submitError && (
            <Alert variant="destructive">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                />
              </svg>
              <AlertDescription className="font-medium">{submitError}</AlertDescription>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel htmlFor="username">Username</FormLabel>
                <FormControl>
                  <Input id="username" variant="raised" autoComplete="username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel htmlFor="password">Password</FormLabel>
                <FormControl>
                  <Input
                    id="password"
                    variant="raised"
                    type="password"
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" size="lg" className="mt-2 w-full gap-x-2" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in with Online Banking'}
            {!isSubmitting && <ArrowRightIcon aria-hidden="true" />}
          </Button>
        </form>
      </Form>

      <p className="text-center text-sm text-fg-muted">
        Not a member yet?{' '}
        <button
          type="button"
          onClick={handleStartGuest}
          className="font-medium text-accent-2 underline decoration-current/40 underline-offset-4 transition-colors hover:decoration-current"
        >
          Apply as a new member
        </button>
      </p>
    </div>
  );
}

export default function LoginForm() {
  return <LoginFormContent />;
}
